import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailRail } from "../../../../../http/hrail/types/rails";
import { useHrailRailsApi } from "../../../../../http/hrail/useRails";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateRailModalDialogButton from "./CreateRailModalDialogButton";
import RailForm from "./RailForm";

type Props = {
  active?: boolean;
};

const Rails: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "rails" });
  const aborter = useRef(new AbortController());
  const railApi = useHrailRailsApi();
  const [itemToEdit, setItemToEdit] = useState<HrailRail>();
  const [itemToDelete, setItemToDelete] = useState<HrailRail>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "rails");
  const isActiveTab = active ?? true;
  const updateVisibility = async (rail: HrailRail, visibility: VisibilityScopeValue) => {
    try {
      await railApi.updateAdminHrailRailVisibility(rail.item.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (rail: HrailRail, newOrder: number) => {
    try {
      await railApi.updateAdminHrailRailOrder(rail.item.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("hrail.rail");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = railApi.useAdminHrailRails(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.rails.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.rails.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateRailModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailRail>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th align="right">{i18n.translation.common.length}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(rail) => (
          <>
            <Table.Td noWrap={true}>{rail.item?.code ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{rail.item?.description ?? ""}</Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(rail.lengthMm, i18n.translation.common.millimeters)}
            </Table.Td>
            <td align="center">
              <Visibility value={rail.visibility} onChange={(value) => updateVisibility(rail, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={rail.order} onUpdate={(newOrder) => updateOrder(rail, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(rail) => setItemToEdit(rail)}
        rowActions={(rail) => [
          getEditAction(i18n, () => setItemToEdit(rail)),
          accessesAction(rail.item),
          getDeleteAction(i18n, () => setItemToDelete(rail), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.rails.edit}>
          <RailForm rail={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.rails.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                railApi
                  .deleteAdminHrailRail(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.rails.delete,
            [itemToDelete.item?.code ?? "", itemToDelete.item?.description ?? ""].filter(Boolean).join(" - ") ||
              itemToDelete.item.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

const formatNullableNumber = (value: number | null | undefined, suffix = "") =>
  typeof value === "number" ? String(value) + suffix : "";

export default Rails;
